import { createNode } from '@glueapp/graph-types';

type HashAlgorithm = 'plain' | 'sha256' | 'sha512' | 'argon2';

async function hashAndCompare(password: string, expectedHash: string, algorithm: HashAlgorithm): Promise<boolean> {
    if (algorithm === 'plain') {
        return password === expectedHash;
    }

    if (algorithm === 'argon2') {
        const argon2 = await import('argon2');
        return argon2.verify(expectedHash, password);
    }

    // SHA-256 / SHA-512 — expected format is salt$hash
    const dollarIndex = expectedHash.indexOf('$');
    if (dollarIndex === -1) {
        return false;
    }

    const salt = expectedHash.slice(0, dollarIndex);
    const storedHash = expectedHash.slice(dollarIndex + 1);

    const crypto = await import('crypto');
    const hashed = crypto
        .createHash(algorithm)
        .update(salt + password)
        .digest('hex');

    // Use timing-safe comparison to prevent timing attacks
    const hashBuffer = Buffer.from(hashed);
    const expectedBuffer = Buffer.from(storedHash);
    if (hashBuffer.length !== expectedBuffer.length) {
        return false;
    }
    return crypto.timingSafeEqual(hashBuffer, expectedBuffer);
}

export const CheckPassword = createNode({
    id: 'CheckPassword',
    name: 'Check Password',
    description:
        'Verify a password against an expected hash. Supports plain text, SHA-256, SHA-512, and Argon2. For Argon2, the salt is embedded in the hash. For SHA variants, the expected format is salt$hash (as produced by Hash Password).',
    category: 'API Server',
    color: '#808080',
    isAsync: true,
    interfaceComponentId: 'check-password-algorithm',
    inPorts: {
        Password: { kind: 'string' },
        'Expected Hash': { kind: 'string' },
    },
    optionalInPorts: ['Expected Hash'],
    outPorts: {
        Valid: { kind: 'boolean' },
    },
    execute: async input => {
        const password = input.inPorts.Password as string | null;
        const expectedHash = input.inPorts['Expected Hash'] as string | null;
        const algorithm = (input.state?.algorithm as HashAlgorithm) || 'argon2';

        if (!password || !expectedHash) {
            return { output: { Valid: false } };
        }

        try {
            const valid = await hashAndCompare(password, expectedHash, algorithm);
            return { output: { Valid: valid } };
        } catch {
            return { output: { Valid: false } };
        }
    },
});
